import React, { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged, User, signOut, signInWithEmailAndPassword, createUserWithEmailAndPassword, sendPasswordResetEmail, EmailAuthProvider, linkWithCredential, updatePassword } from 'firebase/auth';
import { doc, onSnapshot, setDoc, updateDoc, collection, query, where, getDocs, getDoc, deleteDoc, addDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { UserProfile, OperationType } from '../types';
import { handleFirestoreError } from '../utils/error-handler';

interface AuthContextType {
  user: User | null;
  profile: UserProfile | null;
  loading: boolean;
  isAdmin: boolean;
  updateProfile: (data: Partial<UserProfile>) => Promise<void>;
  logout: () => Promise<void>;
  loginWithEmail: (emailOrPhone: string, password: string) => Promise<void>;
  setAccountPassword: (password: string, skipFirestoreUpdate?: boolean) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  setLocalAdmin: (val: boolean) => void;
  registerWithCredentials: (data: {
    displayName: string;
    phone: string;
    email: string;
    secondaryEmail?: string;
    class?: string;
    group?: string;
    batch?: string;
    school?: string;
    password: string;
  }) => Promise<void>;
  syncDeleteToSheets: (uid: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  profile: null,
  loading: true,
  isAdmin: false,
  updateProfile: async () => {},
  logout: async () => {},
  loginWithEmail: async () => {},
  setAccountPassword: async (password: string, skipFirestoreUpdate?: boolean) => {},
  resetPassword: async () => {},
  setLocalAdmin: () => {},
  registerWithCredentials: async () => {},
  syncDeleteToSheets: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [loadTimeout, setLoadTimeout] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);

  // Custom persistent loading screen settings state
  const [loadingSettings, setLoadingSettings] = useState<{
    loadingText?: string;
    loadingSpeed?: number;
    loadingAnimation?: 'gradient_spinner' | 'classic_spinner' | 'pulse_ring' | 'dot_bounce';
  } | null>(null);

  const [appLoading, setAppLoading] = useState(true);
  const bootTimeRef = React.useRef(Date.now());

  // Realtime subscription to loading settings from Firestore settings collection
  useEffect(() => {
    const qSettings = query(collection(db, 'settings'));
    const unsubSettings = onSnapshot(qSettings, (snapshot) => {
      if (!snapshot.empty) {
        const data = snapshot.docs[0].data();
        setLoadingSettings({
          loadingText: data.loadingText,
          loadingSpeed: data.loadingSpeed,
          loadingAnimation: data.loadingAnimation,
        });
      }
    }, (error) => {
      console.error("[AuthContext] Error loaded settings inside AuthProvider:", error);
    });
    return () => unsubSettings();
  }, []);

  // Control appLoading based on actual load event and dynamic speed multiplier/settings delay (in seconds)
  useEffect(() => {
    if (!loading) {
      const activeLoadingSpeed = loadingSettings?.loadingSpeed !== undefined ? loadingSettings.loadingSpeed : 1.5;
      const elapsed = Date.now() - bootTimeRef.current;
      const targetDelay = Math.max(0, (activeLoadingSpeed * 1000) - elapsed);

      const t = setTimeout(() => {
        setAppLoading(false);
      }, targetDelay);

      return () => clearTimeout(t);
    } else {
      setAppLoading(true);
    }
  }, [loading, loadingSettings?.loadingSpeed]);

  const logToTriage = async (message: string, extra: any = {}) => {
    try {
      const logCol = collection(db, 'triage_logs');
      await addDoc(logCol, {
        message,
        extra: {
          ...extra,
          userAgent: navigator?.userAgent || 'unknown',
          url: typeof window !== 'undefined' ? window.location.href : '',
        },
        timestamp: new Date().toISOString(),
      });
      console.log(`[TriageLog] ${message}`, extra);
    } catch (err) {
      console.warn("Telemetry log failed:", err);
    }
  };

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Set a timeout of 5 seconds to detect laggy or unresponsive database/network
    const timer = setTimeout(() => {
      if (loading) {
        setLoadTimeout(true);
      }
    }, 5000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearTimeout(timer);
    };
  }, [loading]);

  // Delayed spinner effect: Only show the visible loading spinner if we've been loading for more than 250ms.
  // This completely eliminates loading UI flashing on super-fast network links and immediate loads.
  useEffect(() => {
    if (loading) {
      const t = setTimeout(() => {
        setShowSpinner(true);
      }, 250);
      return () => clearTimeout(t);
    } else {
      setShowSpinner(false);
    }
  }, [loading]);

  useEffect(() => {
    let unsubProfile: (() => void) | null = null;

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setLoading(true);
        setUser(firebaseUser);
        localStorage.setItem('educore_custom_session_uid', firebaseUser.uid);
        
        logToTriage("onAuthStateChanged triggered with firebaseUser", {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName,
        });

        const userDocRef = doc(db, 'users', firebaseUser.uid);
        
        // Expose a helper to start a simple read-only snapshot listener on the profile
        const startSnapshotListener = (fallbackProfile: UserProfile | null = null) => {
          if (unsubProfile) {
            unsubProfile();
          }
          unsubProfile = onSnapshot(userDocRef, (snap) => {
            console.log(`[AuthContext] Profile snapshot received for ${firebaseUser.uid}, exists: ${snap.exists()}`);
            logToTriage("Profile snapshot listener fired", {
              uid: firebaseUser.uid,
              exists: snap.exists(),
              data: snap.exists() ? snap.data() : null,
              hasFallbackProfile: !!fallbackProfile
            });

            if (snap.exists()) {
              const profileData = snap.data() as UserProfile;
              if (profileData.isDeleted) {
                console.log("[AuthContext] This profile is marked as deleted. Forcing logout...");
                signOut(auth);
                setProfile(null);
                setUser(null);
                setLoading(false);
                return;
              }
              setProfile(profileData);
              setLoading(false);
            } else if (fallbackProfile) {
              setProfile(fallbackProfile);
              setLoading(false);
            }
          }, (err) => {
            console.error("[AuthContext] Profile listener error:", err);
            logToTriage("Profile snapshot listener error", {
              uid: firebaseUser.uid,
              errorMessage: err.message,
              errorCode: err.code
            });

            if (fallbackProfile) {
              setProfile(fallbackProfile);
            }
            setLoading(false);
          });
        };

        // Async profile initialization running once on Auth trigger
        try {
          console.log(`[AuthContext] Initializing profile for ${firebaseUser.uid}...`);
          logToTriage("Fetching profile document via getDoc...", { uid: firebaseUser.uid });
          const docSnap = await getDoc(userDocRef);
          
          logToTriage("getDoc response received", {
            uid: firebaseUser.uid,
            exists: docSnap.exists()
          });

          if (docSnap.exists()) {
            const profileData = docSnap.data() as UserProfile;
            if (profileData.isDeleted) {
              console.log("[AuthContext] This profile is marked as deleted. However, since they have successfully authenticated, we reactivate their account!");
              logToTriage("Reactivating soft-deleted profile because user successfully re-authenticated.", { uid: firebaseUser.uid });
              try {
                await setDoc(doc(db, 'users', firebaseUser.uid), { isDeleted: false }, { merge: true });
                profileData.isDeleted = false;
                logToTriage("Soft-deleted profile successfully reactivated.");
              } catch (reactivateErr: any) {
                console.error("[AuthContext] Error reactivating profile:", reactivateErr);
                logToTriage("Error reactivating soft-deleted profile, forcing logout to be safe", { errorMessage: reactivateErr.message });
                signOut(auth);
                setProfile(null);
                setUser(null);
                setLoading(false);
                return;
              }
            }
            setProfile(profileData);
            setLoading(false);
            // Start simple sync listener
            startSnapshotListener(profileData);
          } else {
            console.log("[AuthContext] Profile does not exist in DB limit. Initiating creation or migration...");
            logToTriage("Profile does not exist. Starting migration check & creation flow...");
            
            // Check if there is an existing user document with the same email (created via credentials registration)
            let existingProfile: UserProfile | null = null;
            let existingDocId: string | null = null;
            if (firebaseUser.email) {
              const cleanEmail = firebaseUser.email.trim().toLowerCase();
              try {
                // First try direct document mapping lookup (Fastest, avoids collection query which has restrictive rules)
                const mappingRef = doc(db, 'auth_mappings', `email_${cleanEmail}`);
                const mappingSnap = await getDoc(mappingRef);
                if (mappingSnap.exists()) {
                  const mData = mappingSnap.data();
                  if (mData && mData.userId && mData.userId !== firebaseUser.uid) {
                    existingDocId = mData.userId;
                    const existingUserRef = doc(db, 'users', existingDocId);
                    const existingUserSnap = await getDoc(existingUserRef);
                    if (existingUserSnap.exists()) {
                      existingProfile = existingUserSnap.data() as UserProfile;
                      console.log(`[AuthContext] Located existing profile for migration using mapping lookup: ${existingDocId}`);
                    }
                  }
                }

                // If direct mapping didn't find anything, we can try the collection query fallback
                if (!existingProfile) {
                  try {
                    const usersRef = collection(db, 'users');
                    const q = query(usersRef, where('email', '==', cleanEmail));
                    const querySnap = await getDocs(q);
                    if (!querySnap.empty) {
                      for (const foundDoc of querySnap.docs) {
                        if (foundDoc.id !== firebaseUser.uid) {
                          existingProfile = foundDoc.data() as UserProfile;
                          existingDocId = foundDoc.id;
                          break;
                        }
                      }
                    }
                  } catch (qErr) {
                    console.warn("[AuthContext] Info: collection query fallback for migration was restricted (non-admin), relying on mappings:", qErr);
                  }
                }
              } catch (err) {
                console.warn("[AuthContext] Info: searching for existing credentials account on Google Sign-In:", err);
              }
            }

            if (existingProfile && existingDocId) {
              console.log(`[AuthContext] Found existing profile with id ${existingDocId} under same email. Migrating to Google UID ${firebaseUser.uid}...`);
              logToTriage("Existing credentials profile found. Initiating migration...", {
                existingDocId
              });

              // Merge/copy existing profile, updating the UID to the new Google UID
              const migratedProfile: UserProfile = {
                ...existingProfile,
                uid: firebaseUser.uid,
                email: firebaseUser.email || existingProfile.email || '',
                displayName: firebaseUser.displayName || existingProfile.displayName || 'Student',
                isDeleted: false,
              };

              try {
                // 1. Create the migrated profile
                await setDoc(userDocRef, migratedProfile);
                logToTriage("Migrated profile written to new UID successfully");

                // 2. Delete the old custom credential profile to avoid duplicate accounts
                try {
                  const oldDocRef = doc(db, 'users', existingDocId);
                  await deleteDoc(oldDocRef);
                  logToTriage("Old credentials document deleted successfully");
                } catch (delErr: any) {
                  console.warn("[AuthContext] Soft warning: Could not delete old duplicate profile:", delErr);
                  logToTriage("Warning: Old credentials document deletion failed", { error: delErr.message });
                }

                // 3. Update auth_mappings for local password and telephone logins
                const primaryEmail = (migratedProfile.email || '').trim().toLowerCase();
                if (primaryEmail) {
                  try {
                    await setDoc(doc(db, 'auth_mappings', `email_${primaryEmail}`), { email: primaryEmail, userId: firebaseUser.uid });
                  } catch (mapErr) {
                    console.warn("[AuthContext] Soft warning: Could not set email map:", mapErr);
                  }
                }

                if (migratedProfile.phone) {
                  const cleanPhone = migratedProfile.phone.trim().replace(/\s+/g, '');
                  const raw11Digit = cleanPhone.startsWith('+88') ? cleanPhone.substring(3) : cleanPhone;
                  try {
                    await setDoc(doc(db, 'auth_mappings', `phone_${raw11Digit}`), { email: primaryEmail, userId: firebaseUser.uid });
                    await setDoc(doc(db, 'auth_mappings', `phone_+88${raw11Digit}`), { email: primaryEmail, userId: firebaseUser.uid });
                  } catch (mapErr) {
                    console.warn("[AuthContext] Soft warning: Could not set phone maps:", mapErr);
                  }
                }

                if (migratedProfile.secondaryEmail) {
                  const cleanSecEmail = migratedProfile.secondaryEmail.trim().toLowerCase();
                  try {
                    await setDoc(doc(db, 'auth_mappings', `email_${cleanSecEmail}`), { email: primaryEmail, userId: firebaseUser.uid });
                  } catch (mapErr) {
                    console.warn("[AuthContext] Soft warning: Could not set secondary email map:", mapErr);
                  }
                }

                // 4. Sync the migrated user profile straight to the Google Sheet
                try {
                  syncToSheets(migratedProfile);
                } catch (sheetErr) {
                  console.warn("[AuthContext] Soft warning: Could not sync migrated profile to sheets:", sheetErr);
                }

                setProfile(migratedProfile);
                setLoading(false);
                startSnapshotListener(migratedProfile);
              } catch (migErr: any) {
                console.error("[AuthContext] Error migrating user profile:", migErr);
                logToTriage("Migration transaction failed", { errorMessage: migErr.message });
                setProfile(migratedProfile);
                setLoading(false);
                startSnapshotListener(migratedProfile);
              }
            } else {
              console.log("[AuthContext] Creating brand new user profile...");
              logToTriage("Creating completely brand new user profile");

              const newProfile: UserProfile = {
                uid: firebaseUser.uid,
                email: firebaseUser.email || '',
                displayName: firebaseUser.displayName || 'Student',
                role: (firebaseUser.email === 'rendernestcom@gmail.com' || firebaseUser.email === 'oaliurrahmansiyam@educore.com') ? 'admin' : 'student',
                enrolledCourses: [],
                createdAt: new Date().toISOString(),
              };
              try {
                await setDoc(userDocRef, newProfile);
                logToTriage("New user profile setDoc succeeded");

                // Create mapping record for secure credentials searches
                if (firebaseUser.email) {
                  const cleanEmail = firebaseUser.email.trim().toLowerCase();
                  await setDoc(doc(db, 'auth_mappings', `email_${cleanEmail}`), { 
                    email: cleanEmail, 
                    userId: firebaseUser.uid 
                  });
                  logToTriage("New user profile email mapping created successfully");
                }

                setProfile(newProfile);
                setLoading(false);
                startSnapshotListener(newProfile);
              } catch (err: any) {
                console.error("[AuthContext] Error creating profile:", err);
                logToTriage("Error creating new user profile document", {
                  errorMessage: err.message,
                  errorCode: err.code
                });
                try {
                  handleFirestoreError(err, OperationType.WRITE, `users/${firebaseUser.uid}`);
                } catch (e) {}
                setProfile(newProfile);
                setLoading(false);
                startSnapshotListener(newProfile);
              }
            }
          }
        } catch (initErr: any) {
          console.error("[AuthContext] Error in profile initialization flow:", initErr);
          logToTriage("Fatal profile initialization flow error caught", {
            errorMessage: initErr.message,
            errorCode: initErr.code
          });

          // FALLBACK: If getDoc fails due to rules / network / permission issues, create a mock/provisional profile 
          // from the firebaseUser metadata to let them log in and edit/complete it safely!
          const fallbackProfile: UserProfile = {
            uid: firebaseUser.uid,
            email: firebaseUser.email || '',
            displayName: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'Student',
            role: (firebaseUser.email === 'rendernestcom@gmail.com' || firebaseUser.email === 'oaliurrahmansiyam@educore.com') ? 'admin' : 'student',
            enrolledCourses: [],
            createdAt: new Date().toISOString(),
          };
          setProfile(fallbackProfile);
          setLoading(false);
          startSnapshotListener(fallbackProfile);
        }
      } else {
        const customUid = localStorage.getItem('educore_custom_session_uid');
        if (customUid) {
          console.log(`[AuthContext] Restoring custom session with UID: ${customUid}`);
          const userDocRef = doc(db, 'users', customUid);
          
          unsubProfile = onSnapshot(userDocRef, (docSnap) => {
            if (docSnap.exists()) {
              const profileData = docSnap.data() as UserProfile;
              if (profileData.isDeleted) {
                console.log("[AuthContext] This custom profile is marked as deleted. Clearing session...");
                localStorage.removeItem('educore_custom_session_uid');
                setProfile(null);
                setUser(null);
                setLoading(false);
                return;
              }
              setProfile(profileData);
              setUser({
                uid: profileData.uid,
                email: profileData.email,
                displayName: profileData.displayName,
              } as User);
              setLoading(false);
            } else if (customUid === 'admin_rendernestcom_bypass' || customUid === 'admin_siyam_bypass') {
              const isSiyam = customUid === 'admin_siyam_bypass';
              const bypassEmail = isSiyam ? 'oaliurrahmansiyam@educore.com' : 'rendernestcom@gmail.com';
              const bypassPass = isSiyam ? 'EducoreBD@3.14159' : 'educoreadminpassword';
              const displayName = isSiyam ? 'Oaliur Rahman Siyam' : 'EduCore Admin';

              console.log(`[AuthContext] Restoring in-memory admin bypass state for ${displayName} and syncing Firebase Auth...`);
              const fallbackAdminProfile: UserProfile = {
                uid: customUid,
                email: bypassEmail,
                displayName: displayName,
                phone: '01700000000',
                role: 'admin',
                class: 'Admin',
                school: 'EduCore Headquarters',
                batch: '2026',
                isDeleted: false,
                enrolledCourses: [],
                createdAt: new Date().toISOString()
              };
              setProfile(fallbackAdminProfile);
              setUser({
                uid: customUid,
                email: bypassEmail,
                displayName: displayName
              } as User);
              setLoading(false);

              // Auto-authenticate with Firebase Auth to get write permissions in Firestore Rules
              signInWithEmailAndPassword(auth, bypassEmail, bypassPass).catch(err => {
                console.warn("[AuthContext] Auto Firebase Auth sign-in failed for bypass admin:", err);
                if (err.code === 'auth/user-not-found' || err.code === 'auth/invalid-credential') {
                  createUserWithEmailAndPassword(auth, bypassEmail, bypassPass)
                    .then(() => console.log("[AuthContext] Auto-registered bypass admin in Firebase Auth successfully"))
                    .catch(createErr => console.error("[AuthContext] Auto-registration failed for bypass admin:", createErr));
                }
              });
            } else {
              localStorage.removeItem('educore_custom_session_uid');
              setProfile(null);
              setUser(null);
              setLoading(false);
            }
          }, (error) => {
            console.error("[AuthContext] Custom profile listener error:", error);
            localStorage.removeItem('educore_custom_session_uid');
            setProfile(null);
            setUser(null);
            setLoading(false);
          });
        } else {
          if (unsubProfile) {
            unsubProfile();
            unsubProfile = null;
          }
          setProfile(null);
          setUser(null);
          setLoading(false);
        }
      }
    });

    return () => {
      unsubscribe();
      if (unsubProfile) unsubProfile();
    };
  }, []);

  const updateProfile = async (data: Partial<UserProfile>) => {
    if (!user) return;
    const userDocRef = doc(db, 'users', user.uid);
    try {
      // Use setDoc with merge: true instead of updateDoc to support seamless updates
      // even for temporary fallback profiles created in memory where no document exists in database yet.
      await setDoc(userDocRef, data, { merge: true });
    } catch (err: any) {
      handleFirestoreError(err, OperationType.WRITE, `users/${user.uid}`);
    }

    // Save secure mapping index for login
    const primaryEmail = data.email || profile?.email || user.email || '';
    if (primaryEmail) {
      if (data.phone) {
        const cleanPhone = data.phone.trim().replace(/\s+/g, '');
        const raw11Digit = cleanPhone.startsWith('+88') ? cleanPhone.substring(3) : cleanPhone;
        try {
          await setDoc(doc(db, 'auth_mappings', `phone_${raw11Digit}`), { email: primaryEmail, userId: user.uid });
          await setDoc(doc(db, 'auth_mappings', `phone_+88${raw11Digit}`), { email: primaryEmail, userId: user.uid });
        } catch (err: any) {
          handleFirestoreError(err, OperationType.WRITE, `auth_mappings/phone_${raw11Digit}`);
        }
      }
      if (data.secondaryEmail) {
        const cleanSecEmail = data.secondaryEmail.trim().toLowerCase();
        try {
          await setDoc(doc(db, 'auth_mappings', `email_${cleanSecEmail}`), { email: primaryEmail, userId: user.uid });
        } catch (err: any) {
          handleFirestoreError(err, OperationType.WRITE, `auth_mappings/email_${cleanSecEmail}`);
        }
      }
    }

    // Automatically sync updated profile data to Google Sheet
    try {
      // Fetch the absolute latest document state from Firestore to prevent race conditions during consecutive/batched profile updates
      let latestDocData: Partial<UserProfile> = {};
      try {
        const latestDocSnap = await getDoc(userDocRef);
        if (latestDocSnap.exists()) {
          latestDocData = latestDocSnap.data() as UserProfile;
        }
      } catch (e) {
        console.warn('[SheetsSync] Failed to fetch latest profile document:', e);
      }

      const fullProfile = {
        ...(profile || {}),
        ...latestDocData,
        ...data,
        uid: user.uid,
        email: primaryEmail
      } as UserProfile;
      syncToSheets(fullProfile);
    } catch (sheetErr) {
      console.error('[SheetsSync] Profile update sync error:', sheetErr);
    }
  };

  const [localAdmin, setLocalAdminState] = useState(() => {
    return localStorage.getItem('educore_admin_bypass') === 'true';
  });

  const setLocalAdmin = (val: boolean) => {
    if (val) {
      localStorage.setItem('educore_admin_bypass', 'true');
    } else {
      localStorage.removeItem('educore_admin_bypass');
    }
    setLocalAdminState(val);
  };

  const logout = async () => {
    localStorage.removeItem('educore_admin_bypass');
    localStorage.removeItem('educore_custom_session_uid');
    setLocalAdminState(false);
    setUser(null);
    setProfile(null);
    await signOut(auth);
  };

  const loginWithEmail = async (emailOrPhone: string, password: string) => {
    let emailToUse = emailOrPhone.trim();
    let resolvedUserId: string | null = null;

    const isSiyamBypass = emailToUse.toLowerCase() === 'oaliurrahmansiyam' && password === 'EducoreBD@3.14159';
    const isDefaultBypass = (emailToUse.toLowerCase() === 'rendernestcom@gmail.com' || emailToUse.toLowerCase() === 'admin') && password === 'educoreadminpassword';

    if (isSiyamBypass || isDefaultBypass) {
      const bypassEmail = isSiyamBypass ? 'oaliurrahmansiyam@educore.com' : 'rendernestcom@gmail.com';
      const bypassPass = isSiyamBypass ? 'EducoreBD@3.14159' : 'educoreadminpassword';
      const displayName = isSiyamBypass ? 'Oaliur Rahman Siyam' : 'EduCore Admin';

      console.log(`[AuthContext] Master Owner credentials match (${bypassEmail})! Authenticating session...`);
      
      let userCredential = null;
      let firebaseUserObj = null;
      try {
        userCredential = await signInWithEmailAndPassword(auth, bypassEmail, bypassPass);
        firebaseUserObj = userCredential.user;
        console.log("[AuthContext] Successfully authenticated master admin with Firebase Auth!");
      } catch (authErr: any) {
        console.warn("[AuthContext] Failed to sign in master admin via Firebase Auth, attempting auto-registration...", authErr);
        if (authErr.code === 'auth/user-not-found' || authErr.code === 'auth/invalid-credential' || authErr.code === 'auth/wrong-password') {
          try {
            userCredential = await createUserWithEmailAndPassword(auth, bypassEmail, bypassPass);
            firebaseUserObj = userCredential?.user || null;
            console.log("[AuthContext] Successfully registered master admin in Firebase Auth!");
          } catch (createErr) {
            console.error("[AuthContext] Failed to register master admin in Firebase Auth:", createErr);
          }
        }
      }

      const adminUID = firebaseUserObj ? firebaseUserObj.uid : (isSiyamBypass ? 'admin_siyam_bypass' : 'admin_rendernestcom_bypass');
      const adminProfile: UserProfile = {
        uid: adminUID,
        email: bypassEmail,
        displayName: displayName,
        phone: '01700000000',
        role: 'admin',
        class: 'Admin',
        school: 'EduCore Headquarters',
        batch: '2026',
        isDeleted: false,
        enrolledCourses: [],
        createdAt: new Date().toISOString()
      };

      if (firebaseUserObj) {
        try {
          await setDoc(doc(db, 'users', adminUID), adminProfile, { merge: true });
        } catch (err) {
          console.warn("[AuthContext] Failed to save bypass profile doc in Firestore, using memory state:", err);
        }
      }

      localStorage.setItem('educore_custom_session_uid', adminUID);
      setLocalAdmin(true);
      setUser(firebaseUserObj || ({
        uid: adminUID,
        email: bypassEmail,
        displayName: displayName
      } as User));
      setProfile(adminProfile);
      return;
    }

    // Check if it is a phone number login (doesn't contain '@')
    if (!emailToUse.includes('@')) {
      const cleanPhone = emailToUse.replace(/\s+/g, '');
      const raw11Digit = cleanPhone.startsWith('+88') ? cleanPhone.substring(3) : cleanPhone;
      
      const mappingRef = doc(db, 'auth_mappings', `phone_${raw11Digit}`);
      const mappingSnap = await getDoc(mappingRef);
      if (mappingSnap.exists()) {
        const d = mappingSnap.data();
        if (d && d.email) {
          emailToUse = d.email;
          resolvedUserId = d.userId || null;
        } else {
          throw new Error('এই ফোন নম্বরটির সাপেক্ষে কোনো ইমেইল পাওয়া যায়নি।');
        }
      } else {
        throw new Error('এই ফোন নম্বর দিয়ে কোনো অ্যাকাউন্ট খুঁজে পাওয়া যায়নি।');
      }
    } else {
      // Contains '@'. Let's check if it corresponds to a secondary email
      const secMappingRef = doc(db, 'auth_mappings', `email_${emailToUse.toLowerCase()}`);
      const secSnap = await getDoc(secMappingRef);
      if (secSnap.exists()) {
        const d = secSnap.data();
        if (d && d.email) {
          emailToUse = d.email;
          resolvedUserId = d.userId || null;
        }
      }
    }

    // Attempt custom Firestore password validation using mapped userId directly (super fast & bypasses any query constraints!)
    if (resolvedUserId) {
      try {
        const userRef = doc(db, 'users', resolvedUserId);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const userData = userSnap.data() as UserProfile;
          if (userData.hasPassword && userData.localPassword) {
            if (userData.localPassword === password) {
              localStorage.setItem('educore_custom_session_uid', userData.uid);
              setUser({
                uid: userData.uid,
                email: userData.email,
                displayName: userData.displayName,
              } as User);
              setProfile(userData);
              return;
            } else {
              throw new Error('আপনার প্রদানকৃত পাসওয়ার্ডটি সঠিক নয়।');
            }
          }
        }
      } catch (err: any) {
        console.error("Direct document fetch error:", err);
      }
    }

    // Query Fallback (for older accounts / other cases)
    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('email', '==', emailToUse));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const userDoc = querySnapshot.docs[0];
        const userData = userDoc.data() as UserProfile;
        if (userData.hasPassword && userData.localPassword) {
          if (userData.localPassword === password) {
            localStorage.setItem('educore_custom_session_uid', userData.uid);
            setUser({
              uid: userData.uid,
              email: userData.email,
              displayName: userData.displayName,
            } as User);
            setProfile(userData);
            return;
          } else {
            throw new Error('আপনার প্রদানকৃত পাসওয়ার্ডটি সঠিক নয়।');
          }
        }
      }
    } catch (err: any) {
      console.warn("[AuthContext] Query fallback failed in loginWithEmail:", err);
      if (err.message === 'আপনার প্রদানকৃত পাসওয়ার্ডটি সঠিক নয়।') {
        throw err;
      }
    }

    try {
      await signInWithEmailAndPassword(auth, emailToUse, password);
    } catch (err: any) {
      if (err.code === 'auth/operation-not-allowed') {
        throw new Error('এই অ্যাকাউন্টে পাসওয়ার্ড সেট করা হয়নি অথবা পাসওয়ার্ডটি সঠিক নয়।');
      }
      throw err;
    }
  };

  const setAccountPassword = async (password: string, skipFirestoreUpdate = false) => {
    // Disabled as per user request
    throw new Error('পাসওয়ার্ড সেট করার অপশনটি নিষ্ক্রিয় করা হয়েছে।');
  };

  const syncToSheets = async (userProf: UserProfile) => {
    // Google Sheets sync is disabled as per user request
    console.log('[SheetsSync] Sheets synchronization is fully disabled.');
  };

  const syncDeleteToSheets = async (uid: string) => {
    // Google Sheets sync is disabled as per user request
    console.log('[SheetsSync] Sheets deletion synchronization is fully disabled.');
  };

  const registerWithCredentials = async (data: {
    displayName: string;
    phone: string;
    email: string;
    secondaryEmail?: string;
    class?: string;
    group?: string;
    batch?: string;
    school?: string;
    password: string;
  }) => {
    throw new Error('পাসওয়ার্ড দিয়ে নতুন রেজিস্ট্রেশন সিস্টেম নিষ্ক্রিয় করা হয়েছে। অনুগ্রহ করে গুগল লগইন ব্যবহার করুন।');
  };

  const resetPassword = async (email: string) => {
    await sendPasswordResetEmail(auth, email);
  };

  const value = {
    user,
    profile,
    loading,
    isAdmin: profile?.role === 'admin' || profile?.email === 'rendernestcom@gmail.com' || profile?.email === 'oaliurrahmansiyam@educore.com' || localAdmin,
    updateProfile,
    logout,
    loginWithEmail,
    setAccountPassword,
    resetPassword,
    setLocalAdmin,
    registerWithCredentials,
    syncDeleteToSheets,
  };

  if (appLoading) {
    const activeText = loadingSettings?.loadingText || 'Powering Up...';
    const activeAnim = loadingSettings?.loadingAnimation || 'gradient_spinner';

    return (
      <div className="min-h-screen bg-zinc-950 text-white flex flex-col items-center justify-center font-sans select-none overflow-hidden p-4">
        {/* Connection issue overlay support */}
        {(isOffline || loadTimeout) ? (
          <div className="max-w-xl bg-amber-500/10 border border-amber-500/20 p-6 rounded-3xl text-center space-y-4 shadow-2xl shadow-amber-500/5 animate-bounce">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-500/10 text-amber-500 mx-auto">
              <svg className="w-6 h-6 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="space-y-1.5 text-center">
              <h3 className="text-amber-500 font-extrabold text-lg tracking-tight">
                {isOffline ? 'নেটওয়ার্ক সংযোগ বিচ্ছিন্ন' : 'কানেকশন পেন্ডিং...'}
              </h3>
              <p className="text-zinc-400 text-sm leading-relaxed max-w-md mx-auto">
                {isOffline 
                   ? 'দয়া করে আপনার ওয়াইফাই বা মোবাইল ইন্টারনেট ডাটা কানেকশন চেক করুন।' 
                   : 'সার্ভার লোড হতে অতিরিক্ত সময় লাগছে। ধীরগতির ইন্টারনেট বা ডাটার কারণে হতে পারে।'}
              </p>
            </div>
            <div className="flex justify-center pt-1">
              <button
                onClick={() => window.location.reload()}
                className="bg-amber-500 hover:bg-amber-600 active:scale-95 text-zinc-950 font-bold px-6 py-2.5 rounded-xl text-sm transition-all shadow-lg shadow-amber-500/20 cursor-pointer"
              >
                আবার চেষ্টা করুন (Retry)
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center space-y-5">
            {activeAnim === 'gradient_spinner' && (
              <div className="relative w-16 h-16">
                <div 
                  className="w-full h-full rounded-full animate-spin" 
                  style={{
                    background: 'conic-gradient(from 0deg, transparent 20%, #10b981 100%)',
                    maskImage: 'radial-gradient(circle, transparent 65%, black 66%)',
                    WebkitMaskImage: 'radial-gradient(circle, transparent 65%, black 66%)',
                  }}
                />
              </div>
            )}

            {activeAnim === 'classic_spinner' && (
              <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
            )}

            {activeAnim === 'pulse_ring' && (
              <div className="relative w-16 h-16 flex items-center justify-center">
                <div className="absolute w-full h-full rounded-full border border-emerald-500/50 animate-ping opacity-75"></div>
                <div className="relative w-10 h-10 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center animate-pulse">
                  <div className="w-4 h-4 rounded-full bg-emerald-500 shadow-md"></div>
                </div>
              </div>
            )}

            {activeAnim === 'dot_bounce' && (
              <div className="flex space-x-2.5 justify-center items-center h-16">
                <div className="h-3.5 w-3.5 bg-emerald-500 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                <div className="h-3.5 w-3.5 bg-emerald-500 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                <div className="h-3.5 w-3.5 bg-emerald-500 rounded-full animate-bounce"></div>
              </div>
            )}

            <p className="text-base font-semibold text-emerald-400/90 tracking-widest animate-pulse font-mono">
              {activeText}
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
